import {
  Box,
  Image,
  Modal,
  Group,
  ActionIcon,
  Text,
  useMantineTheme,
  SimpleGrid,
  Overlay,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState, forwardRef } from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconDownload,
  IconZoomIn,
  IconZoomOut,
  IconMaximize,
} from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface GalleryImage {
  src: string;
  alt?: string;
  thumbnail?: string;
  title?: string;
  description?: string;
}

export interface ImageGalleryProps {
  images: GalleryImage[];
  columns?: number;
  gap?: number | string;
  thumbnailHeight?: number;
  aspectRatio?: number;
  withLightbox?: boolean;
  withDownload?: boolean;
  withZoom?: boolean;
  radius?: number | string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  onImageClick?: (image: GalleryImage, index: number) => void;
}

const ImageGallery = forwardRef<HTMLDivElement, ImageGalleryProps>(
  (
    {
      images,
      columns = 3,
      gap = 'md',
      thumbnailHeight = 200,
      aspectRatio,
      withLightbox = true,
      withDownload = false,
      withZoom = true,
      radius = 'md',
      gradientVariant = 'primary',
      onImageClick,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [opened, { open, close }] = useDisclosure(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const currentImage = images[currentIndex];

    const handleImageClick = (index: number) => {
      if (onImageClick) {
        onImageClick(images[index], index);
      }
      if (withLightbox) {
        setCurrentIndex(index);
        setZoom(1);
        open();
      }
    };

    const handlePrev = () => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      setZoom(1);
    };

    const handleNext = () => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      setZoom(1);
    };

    const handleZoomIn = () => {
      setZoom((prev) => Math.min(prev + 0.5, 3));
    };

    const handleZoomOut = () => {
      setZoom((prev) => Math.max(prev - 0.5, 0.5));
    };

    const handleDownload = async () => {
      if (!currentImage) return;
      const response = await fetch(currentImage.src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentImage.title || `image-${currentIndex + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') close();
    };

    return (
      <>
        <SimpleGrid ref={ref} cols={columns} spacing={gap}>
          {images.map((image, index) => (
            <Box
              key={index}
              pos="relative"
              style={{
                cursor: 'pointer',
                overflow: 'hidden',
                borderRadius: theme.radius[radius as keyof typeof theme.radius] || radius,
                aspectRatio: aspectRatio,
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => handleImageClick(index)}
            >
              <Image
                src={image.thumbnail || image.src}
                alt={image.alt || image.title || `Image ${index + 1}`}
                h={aspectRatio ? undefined : thumbnailHeight}
                fit="cover"
                style={{
                  transition: 'transform 0.3s ease',
                  transform: hoveredIndex === index ? 'scale(1.05)' : 'scale(1)',
                }}
              />
              {hoveredIndex === index && (
                <Overlay
                  gradient={`linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)`}
                  opacity={0.85}
                  zIndex={1}
                >
                  <Box pos="absolute" bottom={0} left={0} right={0} p="sm">
                    {image.title && (
                      <Text size="sm" fw={600} c="white" style={accessibilityStyles}>
                        {image.title}
                      </Text>
                    )}
                    {image.description && (
                      <Text size="xs" c="gray.3" lineClamp={2} style={accessibilityStyles}>
                        {image.description}
                      </Text>
                    )}
                  </Box>
                </Overlay>
              )}
            </Box>
          ))}
        </SimpleGrid>

        {/* Lightbox Modal */}
        <Modal
          opened={opened}
          onClose={close}
          fullScreen
          withCloseButton={false}
          styles={{
            body: { padding: 0, height: '100%' },
            content: { backgroundColor: 'rgba(0,0,0,0.95)' },
          }}
          onKeyDown={handleKeyDown}
        >
          <Box h="100vh" pos="relative" style={{ outline: 'none' }} tabIndex={0}>
            {/* Top bar */}
            <Group
              pos="absolute"
              top={0}
              left={0}
              right={0}
              p="md"
              justify="space-between"
              style={{ zIndex: 100 }}
            >
              <Text c="white" size="sm" style={accessibilityStyles}>
                {currentIndex + 1} / {images.length}
              </Text>
              <Group>
                {withZoom && (
                  <>
                    <ActionIcon
                      variant="subtle"
                      c="white"
                      onClick={handleZoomOut}
                      disabled={zoom <= 0.5}
                    >
                      <IconZoomOut size={20} />
                    </ActionIcon>
                    <Text c="white" size="sm" w={50} ta="center" style={accessibilityStyles}>
                      {Math.round(zoom * 100)}%
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      c="white"
                      onClick={handleZoomIn}
                      disabled={zoom >= 3}
                    >
                      <IconZoomIn size={20} />
                    </ActionIcon>
                  </>
                )}
                {withDownload && (
                  <ActionIcon variant="subtle" c="white" onClick={handleDownload}>
                    <IconDownload size={20} />
                  </ActionIcon>
                )}
                <ActionIcon variant="subtle" c="white" onClick={close}>
                  <IconX size={20} />
                </ActionIcon>
              </Group>
            </Group>

            {/* Image container */}
            <Box
              h="100%"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {currentImage && (
                <Image
                  src={currentImage.src}
                  alt={currentImage.alt || currentImage.title || ''}
                  fit="contain"
                  style={{
                    maxHeight: '85vh',
                    maxWidth: '85vw',
                    transform: `scale(${zoom})`,
                    transition: 'transform 0.2s ease',
                  }}
                />
              )}
            </Box>

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <ActionIcon
                  pos="absolute"
                  left={20}
                  top="50%"
                  style={{ transform: 'translateY(-50%)' }}
                  variant="filled"
                  size="xl"
                  radius="xl"
                  onClick={handlePrev}
                  bg="rgba(255,255,255,0.2)"
                >
                  <IconChevronLeft size={24} color="white" />
                </ActionIcon>
                <ActionIcon
                  pos="absolute"
                  right={20}
                  top="50%"
                  style={{ transform: 'translateY(-50%)' }}
                  variant="filled"
                  size="xl"
                  radius="xl"
                  onClick={handleNext}
                  bg="rgba(255,255,255,0.2)"
                >
                  <IconChevronRight size={24} color="white" />
                </ActionIcon>
              </>
            )}

            {/* Bottom info */}
            {(currentImage?.title || currentImage?.description) && (
              <Box
                pos="absolute"
                bottom={0}
                left={0}
                right={0}
                p="xl"
                ta="center"
                bg="linear-gradient(transparent, rgba(0,0,0,0.8))"
              >
                {currentImage.title && (
                  <Text c="white" size="lg" fw={600} style={accessibilityStyles}>
                    {currentImage.title}
                  </Text>
                )}
                {currentImage.description && (
                  <Text c="gray.4" size="sm" mt="xs" style={accessibilityStyles}>
                    {currentImage.description}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        </Modal>
      </>
    );
  }
);

ImageGallery.displayName = 'ImageGallery';

export default ImageGallery;
